import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

export default function ProductPurchaseSuccess() {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const [result, setResult] = useState<{ product_name: string; amount: number; email: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requested = useRef(false);

  useEffect(() => {
    if (!productId || !sessionId || requested.current) return;
    requested.current = true;
    fetch(`${backendUrl}/api/stripe/public/products/${productId}/checkout/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not confirm your payment');
        setResult(data);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not confirm your payment'));
  }, [productId, sessionId, backendUrl]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-200">
        {error || !sessionId ? (
          <>
            <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h1 className="mt-4 text-lg font-semibold text-gray-900">We couldn't confirm your payment</h1>
            <p className="mt-2 text-sm text-gray-500">
              {error || 'Missing payment details.'} If you were charged, please contact the studio.
            </p>
          </>
        ) : !result ? (
          <div className="py-6">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
            <p className="mt-4 text-sm text-gray-500">Confirming your payment…</p>
          </div>
        ) : (
          <>
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
            <h1 className="mt-4 text-lg font-semibold text-gray-900">Thank you for your purchase!</h1>
            <p className="mt-2 text-sm text-gray-600">
              You paid ${result.amount.toFixed(2)} for {result.product_name}.
            </p>
            {result.email && (
              <p className="mt-1 text-sm text-gray-500">A receipt will be sent to {result.email}.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
